import type { HotelStaticData } from '@/app/components/hotel-detail/common/staticHotelDataAdapter.ts';
import type { OffersData } from '@/app/components/hotel-detail/OffersProvider.tsx';
import type { InitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { HotelDetailShownResponseCodes } from '@/app/common/starTrack/events/utils/starTrack.types.ts';
import {
  getAccommodationType,
  getResponseCodeWithoutRooms,
  getRoomOptionQty,
  getTrackingVcSearchId,
  PRODUCT_DETAILS_RESPONSE_CODE_ROOMS,
} from '@/app/common/tracking/trackingUtils.ts';
import { PAGE_TYPE } from '@/app/common/pageType';

export const HOTEL_DETAIL_SHOWN = 'HOTEL_DETAIL_SHOWN';
export const HOTEL_DETAIL_SHOWN_VERSION = '3.0';

export type HotelDetailShownPayload = {
  accommodationType: string;
  amenitiesFlag: boolean;
  clickId: string;
  currency: string;
  descriptionFlag: boolean;
  hotelImageGalleryQty: number;
  internalHotelId: number;
  isDeduplicaFlag?: boolean | null;
  mealPlanClass: string;
  pricingId: string;
  productDetailKey: string;
  providerId: string;
  responseCode: HotelDetailShownResponseCodes;
  roomId: string;
  roomOptionQty: number;
  roomQty: number;
  searchId: string;
  vcSearchId: string;
  tripId: string;
  totalPrice: number;
  totalPriceEuro: number;
  strikethroughTotalPriceEuro: number;
  flightQty: number;
  hotelTaxesAvailableFlag: boolean;
  transferType: string;
  priceCalendarOverviewFlag: boolean;
  rateId: string;
};

type HotelDetailShownCommonParams = {
  searchId: string | undefined;
  vcSearchId: string | undefined;
  clickId: string | undefined;
  initialParams: InitialParams;
  pricingId: string | undefined;
  hotelStaticData: HotelStaticData | null | undefined;
  offersData: OffersData | undefined;
  localization: Localization;
  pageType: string | undefined;
};

export type HotelDetailShownWithRoomsParams = HotelDetailShownCommonParams & {
  hasRooms: true;
  packageMealPlan: MealPlanType | undefined;
  packageRoom: RoomType | undefined;
  // Passed through as-is (only `isDeduplica` is read, matching the existing cast at the call site).
  roomsData: unknown;
  totalGuestsWithoutInfants: number;
};

export type HotelDetailShownWithoutRoomsParams = HotelDetailShownCommonParams & {
  hasRooms: false;
  isPastCheckin: boolean;
};

export type HotelDetailShownParams = HotelDetailShownWithRoomsParams | HotelDetailShownWithoutRoomsParams;

export const createHotelDetailShownPayload = ({
  searchId,
  vcSearchId,
  clickId,
  initialParams,
  pricingId,
  hotelStaticData,
  offersData,
  localization,
  pageType,
  ...roomsParams
}: HotelDetailShownParams): HotelDetailShownPayload => {
  const hotelImageGalleryQty = hotelStaticData?.images?.length || 0;
  const descriptionFlag = (hotelStaticData?.description?.length ?? 0) > 0;
  const amenitiesFlag = (hotelStaticData?.amenities.length ?? 0) > 0;
  const priceCalendarOverviewFlag = pageType === PAGE_TYPE.HOTEL_DETAIL && !!localization?.configValuesApp?.apexEnabled;
  const tripId = offersData?.transport?.transports?.[0]?.id || '';
  const vcSearchIdToReport = getTrackingVcSearchId(offersData, vcSearchId);

  if (roomsParams.hasRooms) {
    const { packageMealPlan, packageRoom, roomsData, totalGuestsWithoutInfants } = roomsParams;
    return {
      searchId: searchId || '',
      vcSearchId: vcSearchIdToReport,
      tripId,
      internalHotelId: initialParams.internalHotelId || 0,
      clickId: clickId || '',
      responseCode: PRODUCT_DETAILS_RESPONSE_CODE_ROOMS,
      mealPlanClass: packageMealPlan?.boardClass || '',
      currency: localization.currency,
      totalPrice: packageMealPlan?.finalTotalPrice || 0,
      accommodationType: getAccommodationType(hotelStaticData),
      providerId: packageMealPlan?.supplier?.id?.toString() || '',
      totalPriceEuro: packageMealPlan?.finalTotalPriceEUR || 0,
      pricingId: pricingId || '',
      productDetailKey: initialParams.productDetailKey || '',
      isDeduplicaFlag: (roomsData as RoomsServiceResponseType)?.isDeduplica ?? null,
      hotelImageGalleryQty,
      roomQty: offersData?.rooms?.length ?? 0,
      roomOptionQty: getRoomOptionQty(offersData as OffersData),
      descriptionFlag,
      amenitiesFlag,
      roomId: packageRoom?.roomId || '',
      strikethroughTotalPriceEuro: packageMealPlan?.strikeThroughPriceEUR
        ? Math.trunc(
            initialParams.priceType === 'PER_PERSON'
              ? packageMealPlan.strikeThroughPriceEUR * totalGuestsWithoutInfants
              : packageMealPlan.strikeThroughPriceEUR
          )
        : 0,
      flightQty: offersData?.transport?.transports ? offersData.transport.transports.length : 0,
      hotelTaxesAvailableFlag: (packageMealPlan?.excludedCharges?.amount ?? 0) > 0,
      transferType: offersData?.transfer?.outbound?.type || '',
      priceCalendarOverviewFlag,
      rateId: packageMealPlan?.id || '',
    };
  }

  const { isPastCheckin } = roomsParams;
  return {
    searchId: searchId || '',
    vcSearchId: vcSearchIdToReport,
    tripId,
    clickId: clickId || '',
    internalHotelId: initialParams.internalHotelId || 0,
    pricingId: pricingId || '',
    productDetailKey: initialParams.productDetailKey || '',
    responseCode: getResponseCodeWithoutRooms(offersData, isPastCheckin),
    mealPlanClass: '',
    currency: localization.currency,
    totalPrice: 0,
    accommodationType: '',
    providerId: '',
    totalPriceEuro: 0,
    isDeduplicaFlag: false,
    hotelImageGalleryQty,
    roomQty: 0,
    roomOptionQty: 0,
    descriptionFlag,
    amenitiesFlag,
    roomId: '',
    strikethroughTotalPriceEuro: 0,
    flightQty: offersData?.transport?.transports ? offersData.transport.transports.length : 0,
    hotelTaxesAvailableFlag: false,
    transferType: offersData?.transfer?.outbound?.type || '',
    priceCalendarOverviewFlag,
    rateId: '',
  };
};
